/**
 * "Make a bunch of memories" factory to generate sample memories for TIC.
 */

import { faker } from '@faker-js/faker';
import type { Memory } from '@timelyapp/integration-cloud-api-spec';
import { toolConfigurations } from '@timelyapp/integration-cloud-sdk/memory-parser/tool-configurations';
import { constructMemoryIconUrl } from '@timelyapp/integration-cloud-sdk/memory-processors/utils.memories';
import { createMockMemory } from '@timelyapp/integration-cloud-sdk/utils/test-mocks';
import assets from '@timelyapp/static-assets';
import { DateTime } from 'luxon';
import {
  type Profile,
  type ProfileCalendarSession,
  type ProfileSessionEntry,
  profiles,
} from './profiles';
import {
  type DemoMemoryBlock,
  type DemoMemoryPreset,
  DemoMemoryProjectManagementTools,
  type DemoMemorySettings,
} from './types';

export * from './types';

type Options = {
  icpProfile?: true | DemoMemoryPreset;
  date: string;
  maxMemories?: number;
  offsetStartNumHours?: number;
  localizeTime?: boolean;
};

// Day session (a block of time in the day)
interface TimelineBlock {
  note: string;
  aiNotes: {
    concise: string;
    detailed: string;
  };
  startMinute: number;
  endMinute: number;
  contents: Array<ProtoMemory>;
}

interface ProtoMemory {
  toolId: string | undefined;
  url: string | undefined;
  title: string;
  context?: string;
  startSecond: number;
  endSecond: number;
}

/**
 * Generate a set of sample memories with the given query.
 * Useful for mocking data, showing sample data, etc.
 */
export function generateDemoMemories(
  settings: DemoMemorySettings,
  {
    date = DateTime.now().toISODate(),
    offsetStartNumHours = 8,
    maxMemories,
    localizeTime = true,
  }: Options
): { entries: Array<Memory>; blocks: Array<DemoMemoryBlock> } {
  const profile = profiles[settings.preset] || profiles.office;

  // Make seed from date regardless of TZ
  const utcSeed = DateTime.fromISO(date, { zone: 'UTC' }).toMillis();
  faker.seed(utcSeed);

  // Process calendar entries and create meeting sessions
  const calendarBlocks = createCalendarBlocks(profile, settings, offsetStartNumHours);

  // Add a lunch break between 11 AM and 1 PM
  const lunchBlock = createLunchBlock(calendarBlocks, offsetStartNumHours);

  // Fill out rest of day with more blocks, by sampling profile sessions and bounding against existing blocks
  const allBlocks = [
    ...calendarBlocks,
    lunchBlock,
    ...createWorkSessionBlocks(profile, offsetStartNumHours, [...calendarBlocks, lunchBlock]),
  ];

  // Sort blocks by start time
  allBlocks.sort((a, b) => a.startMinute - b.startMinute);

  // Extend block end times to bridge small gaps (less than 5 minutes)
  for (let i = 0; i < allBlocks.length - 1; i++) {
    const currentBlock = allBlocks[i];
    const nextBlock = allBlocks[i + 1];
    const gap = nextBlock.startMinute - currentBlock.endMinute;

    // If there's a gap, extend the current block to cover it (any sized gap)
    // but be careful about special blocks
    const isCurrentLunch = currentBlock.note === 'Lunch Break';
    const isNextLunch = nextBlock.note === 'Lunch Break';
    const isCurrentMeeting = currentBlock.note.startsWith('Meeting:');
    const isNextMeeting = nextBlock.note.startsWith('Meeting:');
    const isLargeGap = gap > 10;

    // Avoid extending large gaps between special blocks (lunch, meetings)
    const shouldExtend =
      gap > 0 &&
      // Always extend small gaps
      (!isLargeGap ||
        // For large gaps, only extend work sessions into other work sessions
        !(isCurrentLunch || isNextLunch || isCurrentMeeting || isNextMeeting));

    if (shouldExtend) {
      currentBlock.endMinute = nextBlock.startMinute;

      // If the block has contents, extend the last content item to fill the gap
      if (currentBlock.contents.length > 0) {
        const lastContent = currentBlock.contents[currentBlock.contents.length - 1];
        lastContent.endSecond = nextBlock.startMinute * 60;
      }
    }
  }

  // Flatten all block contents and create memories
  const allEntries = allBlocks.flatMap(block => block.contents);

  // Apply maxMemories limit if specified
  const limitedEntries = maxMemories ? allEntries.slice(0, maxMemories) : allEntries;

  const referenceDate = localizeTime
    ? DateTime.fromISO(date).startOf('day')
    : DateTime.fromISO(date, { zone: 'UTC' }).startOf('day');

  return {
    entries: createMemoriesFromEntries(limitedEntries, referenceDate.toJSDate(), settings),
    blocks: allBlocks
      .filter(block => block.contents.length > 0)
      .map(block => ({
        note: block.note,
        from: referenceDate.plus({ minutes: block.startMinute }).toISO()!,
        to: referenceDate.plus({ minutes: block.endMinute }).toISO()!,
        aiNotes: block.aiNotes,
      })),
  };
}

/**
 * Process calendar entries and create corresponding meeting sessions
 */
function createCalendarBlocks(
  profile: Profile,
  settings: DemoMemorySettings,
  offsetStartNumHours: number
): TimelineBlock[] {
  if (!profile.meetings || profile.meetings.length === 0) {
    return [];
  }

  // Select a few calendar entries for the day
  const numEvents = faker.number.int({
    min: Math.min(profile.minMeetingsPerDay, profile.meetings.length),
    max: Math.min(profile.minMeetingsPerDay + 2, profile.meetings.length),
  });
  const events = faker.helpers.arrayElements(profile.meetings, numEvents);

  // Work day boundaries
  const workDayStartMinute = 60 * offsetStartNumHours;
  const workDayEndMinute = workDayStartMinute + 60 * 9;

  const blockAllocations: Array<{
    startMinute: number;
    endMinute: number;
    session: ProfileCalendarSession;
  }> = [];

  const randomInt = (options: { min: number; max: number; step?: number }) => {
    const { min, max, step = 1 } = options;
    const range = Math.floor((max - min) / step);
    return faker.number.int({ min: 0, max: range }) * step + min;
  };

  // First allocate blocks
  let eventIndex = 0;
  for (const event of events) {
    // Determine start time for the meeting
    let startMinute: number;

    // Implement smart meeting scheduling logic
    if (events.length > 1) {
      if (eventIndex === 0) {
        // First meeting starts 30-60 minutes after work day start
        startMinute = workDayStartMinute + randomInt({ min: 30, max: 60, step: 30 });
      } else if (events.length > 2 && eventIndex === 1) {
        // Second meeting (when we have more than 2) starts at 13:00, 13:30, or 14:00
        const earlyAfternoonOptions = [13 * 60, 13 * 60 + 30, 14 * 60];
        startMinute = faker.helpers.arrayElement(earlyAfternoonOptions);
      } else if (events.length > 3 && eventIndex === 2) {
        // Third meeting (when we have more than 3) starts at 15:00, 15:30, or 16:00
        const lateAfternoonOptions = [15 * 60, 15 * 60 + 30, 16 * 60];
        startMinute = faker.helpers.arrayElement(lateAfternoonOptions);
      } else {
        // For remaining meetings, ensure they start at 15-minute intervals
        const randomHour = faker.number.int({
          min: offsetStartNumHours + 3,
          max: offsetStartNumHours + 8,
        });
        const randomMinute = faker.helpers.arrayElement([0, 15, 30, 45]);
        startMinute = randomHour * 60 + randomMinute;
      }
    } else {
      // If only one meeting, place it randomly during the day
      startMinute = randomInt({
        min: workDayStartMinute,
        max: workDayEndMinute - event.durationMinutes,
        step: 30,
      });
    }

    eventIndex++; // Increment the event index for the next iteration

    // Otherwise, pick a random time during the day
    let skip = false;
    let iterations = 0;
    while (true) {
      // Check if this time overlaps with any existing calendar sessions
      const overlappingSession = blockAllocations.find(
        block =>
          (startMinute >= block.startMinute && startMinute < block.endMinute) ||
          (block.startMinute >= startMinute &&
            block.startMinute < startMinute + event.durationMinutes)
      );
      const isWithinWorkDay =
        startMinute >= workDayStartMinute &&
        startMinute + event.durationMinutes <= workDayEndMinute;
      if (!overlappingSession && isWithinWorkDay) {
        break; // No overlap, we can use this time
      }

      iterations++;
      if (iterations > 10) {
        skip = true;
        break; // Too many iterations, give up
      }
      startMinute = randomInt({
        min: workDayStartMinute,
        max: workDayEndMinute - event.durationMinutes,
        step: 30,
      });
    }
    if (skip) {
      continue;
    }
    blockAllocations.push({
      startMinute,
      endMinute: startMinute + event.durationMinutes,
      session: event,
    });
  }

  const calendarSessions: TimelineBlock[] = [];

  blockAllocations.sort((a, b) => a.startMinute - b.startMinute);

  // Iterate with index so we can look at prev/next
  for (let i = 0; i < blockAllocations.length; i++) {
    const { startMinute, endMinute, session: event } = blockAllocations[i];
    const durationMinutes = endMinute - startMinute;

    const meetingEntries: Array<ProtoMemory> = [
      {
        toolId: settings.calendarTool,
        url: undefined,
        title: event.title,
        startSecond: startMinute * 60,
        endSecond: endMinute * 60,
      },
    ];

    const minutesGapSinceLastMeeting =
      i > 0 ? startMinute - blockAllocations[i - 1].endMinute : 999;
    const minutesUntilNextMeeting =
      i < blockAllocations.length - 1 ? blockAllocations[i + 1].startMinute - endMinute : 999;

    // Use event's predominantTool or default to a common meeting tool based on the calendar tool
    const predominantTool = settings.meetingTool;

    // Use event's entries or create a default entry for the meeting
    // const entries = [{ tool: predominantTool, title: event.title }, ...event.entries];
    // Create a meeting tool 60% of the time, otherwise only other entries
    const entries =
      faker.number.float() > 0.5
        ? [{ tool: predominantTool, title: event.title }, ...event.entries]
        : event.entries;

    // Randomize startMinute and endMinute:
    // * People are a few mins early or up to 10 mins late to meetings
    // * Actual meeting durations vary by like 30 mins in both directions compared to scheduled
    const actualStartMinute = faker.number.int({
      min: Math.max(startMinute - Math.min(5, minutesGapSinceLastMeeting - 1), workDayStartMinute),
      max: Math.min(startMinute + 3, workDayEndMinute),
    });
    const actualEndMinute = faker.number.int({
      min: Math.max(endMinute - Math.min(durationMinutes / 2, 30), workDayStartMinute),
      max: Math.min(endMinute + Math.min(30, minutesUntilNextMeeting - 1), workDayEndMinute),
    });

    meetingEntries.push(
      ...createEntriesForSession(predominantTool, entries, actualStartMinute, actualEndMinute)
    );

    calendarSessions.push({
      note: `Meeting: ${event.title}`,
      contents: meetingEntries,
      startMinute: actualStartMinute,
      endMinute: actualEndMinute,
      aiNotes: event.aiNotes,
    });
  }

  return calendarSessions;
}

/**
 * Create work session blocks to fill in gaps between calendar blocks
 */
function createWorkSessionBlocks(
  profile: Profile,
  offsetStartNumHours: number,
  existingBlocks: TimelineBlock[]
): TimelineBlock[] {
  if (!profile.sessions || profile.sessions.length === 0) {
    return [];
  }

  const workSessionBlocks: TimelineBlock[] = [];

  // Work day boundaries
  const workDayStartMinute = 60 * offsetStartNumHours;
  const workDayEndMinute = workDayStartMinute + 60 * 9; // 9-hour work day

  // Find available time slots (gaps between meetings)
  const availableSlots: Array<[number, number]> = findAvailableTimeSlots(
    workDayStartMinute,
    workDayEndMinute,
    existingBlocks
  );

  // Shuffle (seeded by date) so a day samples across the whole profile. faker returns
  // a shuffled copy; the original array is left untouched.
  const profileSessions = faker.helpers.shuffle(profile.sessions);

  // Create a larger pool of sessions by repeating them to fill the day
  const sessionsToPlace = [];

  // Try to fill most of the workday by creating multiple session blocks
  // Fill the array with repeated sessions (at least 10 sessions)
  const numSessionsNeeded = Math.max(10, profileSessions.length * 2);

  // Add sessions, cycling through the available ones
  for (let i = 0; i < numSessionsNeeded; i++) {
    sessionsToPlace.push(profileSessions[i % profileSessions.length]);
  }

  // Place each session in an available slot
  for (let i = 0; i < sessionsToPlace.length; i++) {
    const session = sessionsToPlace[i];

    // Find a suitable time slot for this session
    for (let j = 0; j < availableSlots.length; j++) {
      const [slotStart, slotEnd] = availableSlots[j];
      const slotDuration = slotEnd - slotStart;

      // Skip slots that are too short (less than 15 minutes)
      if (slotDuration < 15) continue;

      // Default session length (20-45 minutes, but don't exceed slot duration)
      const maxPossibleLength = Math.min(slotDuration, session.maxLengthMinutes || 120);
      const sessionLength = faker.number.int({
        min: Math.min(45, maxPossibleLength),
        max: maxPossibleLength,
      });

      // We've already ensured the sessionLength fits within slotDuration above

      // Pick a random position within the slot
      const maxStartPosition = slotEnd - sessionLength;
      const sessionStart = faker.number.int({
        min: slotStart,
        max: maxStartPosition,
      });
      const sessionEnd = sessionStart + sessionLength;

      // Create entries for this session
      const entries = createEntriesForSession(
        session.predominantTool,
        session.entries,
        sessionStart,
        sessionEnd
      );

      if (entries.length > 0) {
        // Add the work session block
        workSessionBlocks.push({
          note: session.name,
          contents: entries,
          startMinute: sessionStart,
          endMinute: sessionEnd,
          aiNotes: session.aiNotes,
        });

        // Update available slots
        // Remove current slot and add two new slots (before and after the session)
        availableSlots.splice(j, 1);

        // Add slot before session if large enough
        if (sessionStart - slotStart >= 15) {
          availableSlots.push([slotStart, sessionStart]);
        }

        // Add slot after session if large enough
        if (slotEnd - sessionEnd >= 15) {
          availableSlots.push([sessionEnd, slotEnd]);
        }

        // Sort slots by size (largest first) for next placement
        availableSlots.sort((a, b) => b[1] - b[0] - (a[1] - a[0]));

        break; // Move to next session
      }
    }
  }

  return workSessionBlocks;
}

/**
 * Create a lunch block between 11 AM and 1 PM
 */
function createLunchBlock(
  existingBlocks: TimelineBlock[],
  offsetStartNumHours: number
): TimelineBlock {
  // Convert hours to minutes for the lunch time range (11 AM - 1 PM)
  const lunchEarliestStart = 11 * 60; // 11:00 AM
  const lunchLatestEnd = 13 * 60; // 1:00 PM

  // Lunch duration between 40 and 70 minutes
  const lunchDuration = faker.number.int({ min: 40, max: 70 });

  // Find available slots within lunch time range
  const occupiedSlots = existingBlocks
    .filter(
      block =>
        // Check for any overlap with lunch time range
        block.startMinute < lunchLatestEnd && block.endMinute > lunchEarliestStart
    )
    .map(block => [block.startMinute, block.endMinute] as [number, number])
    .sort((a, b) => a[0] - b[0]);

  // Find possible lunch slots
  const possibleSlots: Array<[number, number]> = [];

  // Start with the full lunch range
  let currentStart = lunchEarliestStart;

  // Adjust for occupied slots
  for (const [slotStart, slotEnd] of occupiedSlots) {
    // If there's a gap before this occupied slot, add it
    if (slotStart > currentStart) {
      possibleSlots.push([currentStart, slotStart]);
    }

    // Move current start to end of this occupied slot
    currentStart = Math.max(currentStart, slotEnd);
  }

  // Add final slot if there's time left
  if (currentStart < lunchLatestEnd) {
    possibleSlots.push([currentStart, lunchLatestEnd]);
  }

  // Filter for slots that are long enough for lunch
  const validLunchSlots = possibleSlots.filter(([start, end]) => end - start >= lunchDuration);

  let lunchStart: number;
  let lunchEnd: number;

  if (validLunchSlots.length > 0) {
    // Choose a random valid slot
    const [slotStart, slotEnd] = faker.helpers.arrayElement(validLunchSlots);

    // Place lunch within the slot
    const latestPossibleStart = slotEnd - lunchDuration;
    lunchStart = faker.number.int({
      min: slotStart,
      max: Math.max(slotStart, latestPossibleStart),
    });
    lunchEnd = lunchStart + lunchDuration;
  } else {
    // No valid slots found, force a lunch break at 12 PM
    lunchStart = 12 * 60; // 12:00 PM
    lunchEnd = lunchStart + lunchDuration;

    // This might overlap with meetings but that's sometimes realistic
  }

  // Create an empty lunch block
  return {
    note: 'Lunch Break',
    startMinute: lunchStart,
    endMinute: lunchEnd,
    contents: [], // Empty block, no activities during lunch
    aiNotes: {
      concise: '',
      detailed: '',
    },
  };
}

/**
 * Helper function to find available time slots between existing blocks
 */
function findAvailableTimeSlots(
  workDayStart: number,
  workDayEnd: number,
  existingBlocks: TimelineBlock[]
): Array<[number, number]> {
  // Create sorted array of occupied time slots
  const occupiedSlots = existingBlocks
    .map(block => [block.startMinute, block.endMinute] as [number, number])
    .sort((a, b) => a[0] - b[0]);

  // Find gaps between occupied slots
  const availableSlots: Array<[number, number]> = [];
  let currentStart = workDayStart;

  for (const [slotStart, slotEnd] of occupiedSlots) {
    // If there's a gap before this occupied slot, add it
    if (slotStart > currentStart) {
      availableSlots.push([currentStart, slotStart]);
    }

    // Move current start to end of this occupied slot
    currentStart = Math.max(currentStart, slotEnd);
  }

  // Add final slot if there's time left in the day
  if (currentStart < workDayEnd) {
    availableSlots.push([currentStart, workDayEnd]);
  }

  // Sort by slot duration (largest first)
  return availableSlots.sort((a, b) => b[1] - b[0] - (a[1] - a[0]));
}

/**
 * Create entries from a session
 */
function createEntriesForSession(
  predominantTool: string,
  entries: Array<ProfileSessionEntry>,
  startMinute: number,
  endMinute: number
): Array<ProtoMemory> {
  const result: Array<ProtoMemory> = [];

  // If no entries, return empty array
  if (!entries || entries.length === 0) {
    return result;
  }

  /// Oscillates between 0 and 1 with period of `periodMinutes`
  const pattern = (tSeconds: number, periodMinutes: number): number => {
    return Math.sin((tSeconds * Math.PI) / (periodMinutes * 60)) * 0.5 + 0.5;
  };

  // Current minute in the session
  let currentSecond = startMinute * 60;
  const endSecond = endMinute * 60;

  // Filter entries by predominant tool
  const predominantEntries = entries.filter(entry => entry.tool === predominantTool);
  const secondaryEntries = entries.filter(entry => entry.tool !== predominantTool);

  // Check if we have valid entries to work with
  if (predominantEntries.length === 0 && secondaryEntries.length === 0) {
    return result;
  }

  // If no predominant entries exist, all entries are considered secondary
  const hasPredominantEntries = predominantEntries.length > 0;
  const hasSecondaryEntries = secondaryEntries.length > 0;

  // Track current focus - start with predominant mode if available
  let inPredominantMode = hasPredominantEntries && faker.datatype.boolean(0.7); // 70% chance to start in predominant mode

  // Force to available mode if one category is empty
  if (inPredominantMode && !hasPredominantEntries) {
    inPredominantMode = false;
  } else if (!inPredominantMode && !hasSecondaryEntries) {
    inPredominantMode = true;
  }

  let currentPredominantEntry =
    predominantEntries.length > 0 ? faker.helpers.arrayElement(predominantEntries) : null;
  let currentSecondaryEntry =
    secondaryEntries.length > 0 ? faker.helpers.arrayElement(secondaryEntries) : null;

  // Last switch times
  let lastToolModeSwitch = currentSecond;
  let lastPredominantEntrySwitch = currentSecond;
  let lastSecondaryEntrySwitch = currentSecond;

  const MIN_FILE_SWITCHING_PERIOD_S = 60;

  while (currentSecond < endSecond) {
    // Calculate pattern values based on time
    const elapsedSeconds = currentSecond - startMinute * 60;
    const concentration = pattern(elapsedSeconds, 15);
    const predominantFileSwitching = pattern(elapsedSeconds, 10);
    const secondaryFileSwitching = pattern(elapsedSeconds, 3);

    // Only consider mode switching if both modes have entries
    if (hasPredominantEntries && hasSecondaryEntries) {
      // Determine if we should switch between predominant and secondary modes
      // Higher concentration = higher chance to stay in predominant mode
      if (
        currentSecond - lastToolModeSwitch > 180 && // At least 3 minutes since last mode switch
        faker.number.float() > (inPredominantMode ? concentration : 1 - concentration)
      ) {
        inPredominantMode = !inPredominantMode;
        lastToolModeSwitch = currentSecond;
      }
    }

    // Determine if we should switch entries within the current mode
    if (inPredominantMode && predominantEntries.length > 1) {
      // Switch files within predominant tool based on predominantFileSwitching
      // Lower predominantFileSwitching = higher chance to switch
      if (
        currentSecond - lastPredominantEntrySwitch > MIN_FILE_SWITCHING_PERIOD_S &&
        faker.number.float() > predominantFileSwitching
      ) {
        const oldEntry = currentPredominantEntry;
        // Pick a different entry than the current one
        do {
          currentPredominantEntry = faker.helpers.arrayElement(predominantEntries);
        } while (currentPredominantEntry === oldEntry && predominantEntries.length > 1);

        lastPredominantEntrySwitch = currentSecond;
      }
    } else if (!inPredominantMode && secondaryEntries.length > 1) {
      // Switch between secondary tools based on secondaryFileSwitching
      // Lower secondaryFileSwitching = higher chance to switch
      if (
        currentSecond - lastSecondaryEntrySwitch > 30 && // At least 30 seconds since last switch
        faker.number.float() > secondaryFileSwitching
      ) {
        const oldEntry = currentSecondaryEntry;
        // Pick a different entry than the current one
        do {
          currentSecondaryEntry = faker.helpers.arrayElement(secondaryEntries);
        } while (currentSecondaryEntry === oldEntry && secondaryEntries.length > 1);

        lastSecondaryEntrySwitch = currentSecond;
      }
    }

    // Determine the current entry and how long to stay on it
    const currentEntry =
      (inPredominantMode ? currentPredominantEntry : currentSecondaryEntry) ?? entries[0];

    // Determine duration based on mode
    let durationSeconds;
    if (inPredominantMode) {
      // Predominant tools get longer focus times (2-10 minutes)
      durationSeconds = Math.max(120, Math.floor(concentration * 600));
    } else {
      // Secondary tools get shorter focus times (30s-3 minutes)
      durationSeconds = Math.max(30, Math.floor(secondaryFileSwitching * 180));
    }

    // Don't exceed the session end time
    const endEntrySecond = Math.min(currentSecond + durationSeconds, endSecond);

    // Check if there's already an identical entry in the result that we can extend
    const lastEntry = result.length > 0 ? result[result.length - 1] : null;
    if (
      lastEntry &&
      lastEntry.toolId === currentEntry.tool &&
      lastEntry.title === currentEntry.title &&
      lastEntry.endSecond === currentSecond
    ) {
      // Extend the existing entry instead of creating a new one
      lastEntry.endSecond = endEntrySecond;
    } else {
      // Create a new memory entry
      result.push({
        toolId: currentEntry.tool,
        url: currentEntry.url,
        title: currentEntry.title,
        context: currentEntry.tool,
        startSecond: currentSecond,
        endSecond: endEntrySecond,
      });
    }

    // Move forward by the entry duration
    currentSecond = endEntrySecond;
  }

  return result;
}

/**
 * Create actual memory objects from the timeline entries
 */
function createMemoriesFromEntries(
  protoEntries: Array<ProtoMemory>,
  baseDate: Date,
  settings?: DemoMemorySettings
): Array<Memory> {
  // Sort entries by start time
  protoEntries.sort((a, b) => a.startSecond - b.startSecond);

  const combinedEntries: Array<ProtoMemory> = [];
  for (const entry of protoEntries) {
    // Look back up to 10 entries
    let extended = false;
    for (let lookbackSteps = 0; lookbackSteps < 10; lookbackSteps++) {
      const lookbackIndex = combinedEntries.length - 1 - lookbackSteps;
      if (lookbackIndex < 0) break;

      const lookbackEntry = combinedEntries[lookbackIndex];
      if (
        lookbackEntry.toolId === entry.toolId &&
        lookbackEntry.title === entry.title &&
        entry.startSecond - lookbackEntry.endSecond < 60
      ) {
        // Combine entries
        lookbackEntry.endSecond = Math.max(lookbackEntry.endSecond, entry.endSecond);
        extended = true;
        break;
      }
    }
    if (extended) continue;
    combinedEntries.push({ ...entry });
  }

  // Create memories from combined entries
  const memories: Array<Memory> = [];
  for (const entry of combinedEntries) {
    // Create memory from the entry
    memories.push(createMemoryFromProtoEntry(baseDate, entry, settings));
  }

  return memories;
}

// Helper function to create a regular tool memory
function createMemoryFromProtoEntry(
  baseDate: Date,
  entry: ProtoMemory,
  settings?: DemoMemorySettings
): Memory {
  const { toolId, tool, url, icon, isIntegrationMemory } = determineToolMeta(entry, settings);

  const startSecond = entry.startSecond;
  const endSecond = entry.endSecond;

  const startMinute = Math.floor(startSecond / 60);
  const durationMins = Math.ceil((endSecond - startSecond) / 60);

  const window_tool =
    url == null
      ? null
      : {
          icon_url: 'https://icons.timelyapp.com/apps/mac/Google Chrome',
          id: 'google_chrome',
          raw: 'Google Chrome',
          title: 'Google Chrome',
        };

  return createMockMemory(
    { baseDate, startOffsetMins: startMinute, durationMins },
    {
      id: faker.number.int(),
      title: entry.title,
      color: tool?.color,
      icon_url: icon,
      url,
      context: null,
      tool,
      window_tool,
      tracker_source: isIntegrationMemory ? null : 'mac',
      from_timely: isIntegrationMemory,
    }
  );
}

const ICONS: Record<string, any> = {
  microsoft_outlook_calendar: 'https://integrations.app.timelyapp.com/images/tools/office365.png',
  microsoft_teams: 'https://icons.timelyapp.com/apps/mac/Microsoft Teams',
  zoom: 'https://icons.timelyapp.com/apps/mac/zoomus',
  jira: assets.icons['jira_128.png'],
  netsuite: 'https://icons.timelyapp.com/favicons/system.netsuite.com',
  oracle: 'https://icons.timelyapp.com/favicons/docs.oracle.com',
  workday: 'https://icons.timelyapp.com/favicons/workday.com',
  sap: 'https://icons.timelyapp.com/favicons/sap.com',
  // The icon service has no SharePoint app icon; the Microsoft favicon is the closest match.
  microsoft_sharepoint: 'https://icons.timelyapp.com/favicons/microsoft.com',
  sharepoint: 'https://icons.timelyapp.com/favicons/microsoft.com',
};
// Display names for demo-only tool ids that have no entry in toolConfigurations.
// Without this the fallback capitalises the id ("Sap", "Netsuite").
const TOOL_TITLES: Record<string, string> = {
  netsuite: 'NetSuite',
  oracle: 'Oracle',
  workday: 'Workday',
  sap: 'SAP',
  microsoft_sharepoint: 'SharePoint',
  sharepoint: 'SharePoint',
};
const EXTERNAL_TOOLS = ['google_calendar', 'microsoft_outlook_calendar'];

const urlForToolAndTitle = (toolId: string, title: string) => {
  const toolConfig = toolConfigurations[toolId]?.['tool'];
  const sluggedTitle = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  if (toolConfig?.domains) {
    return `https://${toolConfig.domains[0]}/${sluggedTitle}`;
  }
  return null;
};

function determineToolMeta(entry: ProtoMemory, settings?: DemoMemorySettings) {
  if (entry.toolId) {
    let toolId = entry.toolId;

    // If project management tool is specified, force all memories from PM-tools to be of the chosen type
    const pmTools = DemoMemoryProjectManagementTools.map(({ value }) => value) as string[];

    // If set, force a specific PM tool
    if (!!settings?.projectManagementTool && pmTools.includes(toolId)) {
      toolId = settings.projectManagementTool;
    }

    const toolConfig = toolConfigurations[toolId]?.['tool'];
    const title =
      toolConfig?.title ?? TOOL_TITLES[toolId] ?? `${toolId[0].toUpperCase()}${toolId.slice(1)}`;

    const tool = {
      id: toolId,
      title,
      color: toolConfig?.color,
      category: toolConfig?.category,
      raw: title,
    };

    const icon =
      ICONS[toolId] ??
      constructMemoryIconUrl({
        defaultIconUrl: 'https://icons.timelyapp.com/apps/mac/Timely',
        tool,
      });
    const isIntegrationMemory = EXTERNAL_TOOLS.includes(toolId);
    const url = isIntegrationMemory ? null : urlForToolAndTitle(toolId, entry.title);

    return { toolId, icon, tool, isIntegrationMemory, url };
  }
  if (!entry.url) {
    return { toolId: null, icon: null, tool: null, isIntegrationMemory: false, url: null };
  }
  // Use URL
  // tool is domain, parse as url
  let toolId;
  try {
    const url = new URL(entry.url);
    toolId = url.hostname;
  } catch (e) {
    return { toolId: null, icon: null, tool: null, isIntegrationMemory: false, url: null };
  }
  const isIntegrationMemory = false;
  const icon = constructMemoryIconUrl({
    url: entry.url,
    defaultIconUrl: 'https://icons.timelyapp.com/favicon/timely.com',
  });
  const tool = {
    id: toolId,
    title: toolId,
    color: null,
    category: null,
    raw: toolId,
  };
  return { toolId, icon, tool, isIntegrationMemory, url: entry.url };
}
